import React, { useState, useEffect } from "react";
import { connect } from "react-redux";

import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  tooltipClasses,
} from "@material-ui/core";

import * as actions from "../../store/actions/actionIndex";
import { maskedNumber } from "../../helpers/utility";

const columns = [
  { id: "campaign_name", label: "Campaign", minWidth: 100 },
  { id: "cust_ph_no", label: "Customer Phone", minWidth: 100, align: "right" },
  { id: "q_enter_time", label: "Queue Enter Time", minWidth: 120, align: "right" },
  { id: "totalTime", label: "Duration(secs)", minWidth: 50, align: "right" },
  { id: "skill", label: "Skill", minWidth: 100, align: "right" },
  { id: "UniqueId", label: "Unique Id", minWidth: 120, align: "right" },
];

const useStyles = makeStyles(() => ({
  root: {
    width: "100%",
  },
  container: {
    maxHeight: 440,
  },
  closeButton: {
    float: "right",
    border: "1px solid #137dcc",
    borderRadius: 50,
    width: 25,
    marginInlineEnd: -12,
    marginTop: -4,
    cursor: "pointer",
  },

  // Tooltip styling via tooltipClasses
  tooltip: {
    backgroundColor: "#1f2937",
    color: "#fff",
    fontSize: "12px",
    maxWidth: 400,
    wordBreak: "break-all",
  },
  arrow: {
    color: "#1f2937",
  },
}));

function CallInQueue(props) {
  const classes = useStyles();
  const { agentId, onQueueCalls } = props;

  useEffect(() => {
    const interval = setInterval(() => {
      onQueueCalls(agentId);
    }, 10000);

    return () => clearInterval(interval);
  }, [agentId, onQueueCalls]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const rows = Array.from(props.queueCallData || []);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  return (
    <Paper className={classes.root}>
      <TableContainer className={classes.container}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center" colSpan={6}>
                Queue Details
                <span
                  onClick={() => props.showQueueDetails(false)}
                  className={classes.closeButton}
                >
                  X
                </span>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row) => (
                <TableRow
                  hover
                  tabIndex={-1}
                  key={row.session_id}
                >
                  {columns.map((column) => {
                    const value = row[column.id];

                    // PHONE NUMBER
                    if (column.id === "cust_ph_no") {
                      const shouldMask =
                        props.facilityConf.enable_number_mask === "1";

                      return (
                        <TableCell key={column.id} align={column.align}>
                          {shouldMask
                            ? maskedNumber(
                                value,
                                props.facilityConf.mask_start_pos,
                                props.facilityConf.mask_end_pos
                              )
                            : value}
                        </TableCell>
                      );
                    }

                    // UNIQUE ID HANDLING
                    if (column.id === "UniqueId") {
                      if (!value) {
                        return (
                          <TableCell key={column.id} align={column.align}>
                            Null
                          </TableCell>
                        );
                      }

                      const maxLength = 10;
                      const shortText =
                        value.length > maxLength
                          ? value.substring(0, maxLength) + "..."
                          : value;

                      return (
                        <TableCell key={column.id} align={column.align}>
                          <Tooltip
                            title={value}
                            arrow
                            classes={{
                              tooltip: classes.tooltip,
                              arrow: classes.arrow,
                            }}
                          >
                            <span style={{ cursor: "pointer" }}>
                              {shortText}
                            </span>
                          </Tooltip>
                        </TableCell>
                      );
                    }

                    // OTHER FIELDS
                    return (
                      <TableCell key={column.id} align={column.align}>
                        {value ?? "-"}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
}

const mapStateToProps = (state) => ({
  loading: state.queueCalls.loading,
  error: state.queueCalls.error,
  agentId: state.agentData.agentId,
  queueCallData: state.queueCalls.queueCallData,
  facilityConf: state.agentConfig.facilityConf,
});

const mapDispatchToProps = (dispatch) => ({
  onQueueCalls: (agentId) => dispatch(actions.queueCalls(agentId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CallInQueue);
